import { action, computed, makeObservable, observable } from 'mobx';

import { CollectionDriveInfo } from '@/collection/entity/CollectionDriveInfo';
import { CollectionType } from '@/collection/entity/CollectionType';
import { filterProductsWithStereo } from '@/collection/helpers/filterProductsWithStereo';
import { formatDate } from '@/common/helpers/formatDate';
import Sort from '@/common/state/Sort';
import { SortDirection } from '@/common/types/sort';
import { ContactDataLink } from '@/contacts/entity/ContactDataLink';
import { ContactDataLinkModel } from '@/contacts/entity/ContactDataLink';
import { CollectionItem } from '@/drive/entities/DriveItem/CollectionItem';
import { DriveItemType } from '@/drive/entities/DriveItem/DriveItem';
import { createEmptyForm } from '@/forwarding/types/ForwardForm';
import {
  TYPE_DIAMOND_COLORLESS,
  TYPE_GEM,
  TYPE_JEWELRY,
  TYPE_LGD_ROUGH,
  TYPE_ROUGH,
} from '@/product/constants/productTypes';
import { ProductType } from '@/product/constants/productTypes';
import Product, { ProductModel } from '@/product/entity/Product';
import ProductFactory from '@/product/entity/ProductFactory';
import ProductViewSettings, { ProductViewSettingsModel } from '@/product/entity/ProductViewSettings';
import { getAccessById } from '@/security/constants/access';
import { User } from '@/user/entity/User';

import { viewTemplateRepository } from '../repository/ViewTemplateRepository';

export interface CollectionPlainObject {
  id?: string;
  createdAt?: string;
  updatedAt?: string;
  type?: CollectionType;
  title?: string;
  description?: Nullable<string>;
  active?: Nullable<boolean>;
  settings?: Partial<Omit<ProductViewSettingsModel, 'sort'>> & { sort?: Nullable<Record<string, SortDirection>> };
  userId?: Nullable<number>;
  products?: ProductModel[];
  drive?: CollectionDriveInfo;
  contactDataLink?: Nullable<ContactDataLinkModel>;
  _links?: Nullable<{ href: string; rel: string }[]>;
}

export const COLLECTION_TYPE_DIAMOND = CollectionType.Diamond;
export const COLLECTION_TYPE_JEWELRY = CollectionType.Jewelry;
export const COLLECTION_TYPE_ROUGH = CollectionType.Rough;
export const COLLECTION_TYPE_GEM = CollectionType.Gem;

export const COLLECTION_MAX_COUNT = 1000;

export default class Collection {
  id!: string;

  drive!: CollectionDriveInfo;

  title = '';

  description = '';

  settings!: ProductViewSettings;

  defaultSettings!: ProductViewSettings;

  products: Product[] = [];

  type!: CollectionType;

  user: Nullable<User> = null;

  createdAt?: string;

  updatedAt?: Nullable<string>;

  manualSorting!: number[];

  contactDataLink?: Nullable<ContactDataLink>;

  dilogLink?: string;

  productType!: ProductType;

  static getTypeByProductType(productType: ProductType): CollectionType {
    if (productType === TYPE_JEWELRY) {
      return COLLECTION_TYPE_JEWELRY;
    }

    if (productType === TYPE_ROUGH || productType === TYPE_LGD_ROUGH) {
      return COLLECTION_TYPE_ROUGH;
    }

    if (productType === TYPE_GEM) {
      return COLLECTION_TYPE_GEM;
    }

    return COLLECTION_TYPE_DIAMOND;
  }

  constructor(modelData: CollectionPlainObject = {}, productType: ProductType = TYPE_DIAMOND_COLORLESS) {
    makeObservable(this, {
      id: false,
      user: false,
      createdAt: false,
      updatedAt: false,
      manualSorting: false,
      toForwardInfo: false,
      hasProduct: false,
      getProductById: false,
      prevProductFor: false,
      nextProductFor: false,
      serialize: false,
      drive: observable,
      title: observable,
      description: observable,
      settings: observable,
      defaultSettings: observable,
      products: observable,
      type: observable,
      contactDataLink: observable,
      dilogLink: observable,
      productType: observable,
      length: computed,
      isEmpty: computed,
      productIds: computed,
      isLoaded: computed,
      driveItem: computed,
      access: computed,
      readableStatus: computed,
      createdAtFormatted: computed,
      activeSetupPreset: computed,
      productsWithStereo: computed,
      hasStereo: computed,
      assignFrom: action,
      setManualSorting: action,
    });

    this.assignFrom(modelData, productType);
  }

  get length() {
    return this.products ? this.products.length : 0;
  }

  get isEmpty() {
    return !this.products || this.products.length === 0;
  }

  get productIds(): number[] {
    return this.products ? this.products.map((p) => p.id) : [];
  }

  get isLoaded() {
    return this.products && this.products.every((p) => p.isLoaded);
  }

  get driveItem(): CollectionItem {
    return new CollectionItem({
      id: this.drive.collectionNodeId,
      type: DriveItemType.Collection,
      title: this.title,
      description: this.description,
      collectionId: this.id,
      collectionType: this.type,
      count: this.length,
      access: this.drive.access,
      dataAccess: this.drive.dataAccess,
      canEdit: this.drive.canEdit,
      canManageAccess: this.drive.canManageAccess,
    });
  }

  get access() {
    return this.drive.access;
  }

  get readableStatus(): string {
    return getAccessById(this.access, 'collection')?.title ?? '';
  }

  get createdAtFormatted(): Nullable<string> {
    return this.createdAt ? formatDate.toHumanDate(this.createdAt) : null;
  }

  get activeSetupPreset() {
    const spList = this.products
      .map((p) => (p.mediaCollection
        ? p.mediaCollection.getSuitableSPFromSPList(this.settings.resultedSetupPresets)
        : null))
      .filter((sp) => Boolean(sp));

    return spList?.[0]?.id ?? null;
  }

  get productsWithStereo(): Product[] {
    return filterProductsWithStereo(this.products, this.settings.resultedSetupPresets);
  }

  toForwardInfo() {
    return {
      preview: { type: DriveItemType.Collection, collectionId: this.id },
      ...createEmptyForm(),
      title: this.title ?? '',
      description: this.description ?? '',
    };
  }

  get hasStereo() {
    return this.productsWithStereo.length === this.products.length;
  }

  hasProduct(product: Product): boolean {
    if (!this.products || this.products.length === 0) {
      return false;
    }

    return Boolean(this.getProductById(product.id));
  }

  getProductById = (id: number) => {
    return this.products.find((p) => p.id === id);
  };

  prevProductFor(product: Product): Nullable<Product> {
    if (!this.hasProduct(product)) {
      return null;
    }

    const i = this.products.findIndex((p) => p.id === product.id);

    if (i === 0) {
      return this.products[this.products.length - 1];
    }

    return this.products[i - 1];
  }

  nextProductFor(product: Product): Nullable<Product> {
    if (!this.hasProduct(product)) {
      return null;
    }

    const i = this.products.findIndex((p) => p.id === product.id);

    if (i === this.products.length - 1) {
      return this.products[0];
    }

    return this.products[i + 1];
  }

  serialize(): CollectionPlainObject {
    const json: CollectionPlainObject = {
      title: this.title,
      description: this.description,
    };

    if (this.settings) {
      json.settings = this.settings.serialize();
    }

    return json;
  }

  // todo
  static getForcedCollectionSettings(collectionType?: CollectionType): Partial<ProductViewSettings> {
    if (collectionType === COLLECTION_TYPE_JEWELRY) {
      return {
        isSingleScale: false,
        isGrayscale: false,
      };
    }

    return {};
  }

  /**
   * Copy contents of plain object to entity instance
   */
  assignFrom(modelData: CollectionPlainObject, productType: ProductType = TYPE_DIAMOND_COLORLESS): Collection {
    this.productType = productType;

    // todo should use Sort object
    const settingsSort = modelData && modelData.settings && modelData.settings.sort;

    if (settingsSort && settingsSort[Sort.SORT_MEDIA_RELEVANCE]) {
      delete settingsSort[Sort.SORT_MEDIA_RELEVANCE];
      settingsSort[Sort.SORT_MANUAL] = Sort.DIRECTION_DESC;
    }

    if (modelData.id !== undefined) {
      this.id = modelData.id;
    }

    if (modelData.drive !== undefined) {
      this.drive = modelData.drive;
    }

    if (modelData.createdAt !== undefined) {
      this.createdAt = modelData.createdAt;
    }

    if (modelData.updatedAt !== undefined) {
      this.updatedAt = modelData.updatedAt;
    }

    if (modelData.type !== undefined) {
      this.type = modelData.type;
    }

    if (modelData.title !== undefined) {
      this.title = modelData.title;
    }

    if (modelData.description !== undefined) {
      this.description = modelData.description ?? '';
    }

    if (modelData.userId !== undefined) {
      this.user = new User({ id: modelData.userId });
    }

    if (modelData.contactDataLink) {
      this.contactDataLink = new ContactDataLink(modelData.contactDataLink);
    }

    if (modelData._links) {
      const dilogLinkData = modelData._links.find(({ rel }) => rel === 'dilog.report');

      if (dilogLinkData && dilogLinkData.href) {
        this.dilogLink = dilogLinkData.href;
      }
    }

    const forcedSettings = Collection.getForcedCollectionSettings(modelData.type);

    if (modelData.settings !== undefined) {
      this.settings = new ProductViewSettings(
        {
          ...modelData.settings,
          viewTemplate: modelData.settings.viewTemplate,
        },
        forcedSettings,
      );
    } else {
      this.settings = new ProductViewSettings(
        { viewTemplate: viewTemplateRepository.getDefaultViewTemplateId(productType) },
        forcedSettings,
      );
    }

    this.defaultSettings = new ProductViewSettings(this.settings.serialize());

    if (modelData.products) {
      this.products = modelData.products.map((p) => ProductFactory.make(productType, p));
      this.setManualSorting(this.products.map((p) => p.id));
    }

    return this;
  }

  setManualSorting(order: number[]) {
    this.manualSorting = order;
  }
}
